import { Button } from './Button';
import { isConflictError } from './conflictError';

export interface QueryErrorProps {
  /** What the request threw. Nothing is rendered while this is null, so `query.error` can be passed straight in. */
  readonly error: Error | null;
  /**
   * What could not be read, in the user's words: "The price timeline", "The targets".
   *
   * Giving it says the failure replaced content the person came to read, and the
   * message becomes "{subject} could not be read: …" followed by a reminder that
   * nothing being shown is not the same as there being nothing there.
   */
  readonly subject?: string;
  /** Asks the request to run again. The button is drawn only when this is given. */
  readonly onRetry?: () => void;
  /** Whether a retry is already in flight. */
  readonly retrying?: boolean;
  /** The retry button's label. Defaults to "Try again". */
  readonly retryLabel?: string;
  /**
   * Reloads what the server holds now, offered only for a version conflict (409).
   *
   * A retry would resend the same refused version; this is the way out.
   */
  readonly onReload?: () => void;
  /** The reload button's label. Defaults to "Reload current values". */
  readonly reloadLabel?: string;
  /** The test hook for this particular failure. */
  readonly testId?: string;
}

/** The status and support reference of a request error that carries one, for the disclosure. */
function supportDetails(error: Error): { status: number; reference: string | null } | null {
  if (!('status' in error) || typeof error.status !== 'number' || !('reference' in error)) {
    return null;
  }
  const { status, reference } = error;
  return {
    status,
    reference: typeof reference === 'string' && reference !== '' ? reference : null,
  };
}

/**
 * A failed request, said plainly, with the way to try again.
 *
 * An empty state is an assertion: it says the server answered and there was
 * nothing there. Rendering one when the request actually failed tells a manager
 * that no category has a target, which is a statement about their pricing
 * policy, and they may believe it. This says the opposite: that the answer is
 * unknown, why, and what to do about it.
 *
 * It is one alert rather than a toast because it replaces content the person
 * came to read, and it carries the retry because the commonest cause is a
 * dropped connection rather than anything wrong with the workspace. The whole
 * block is the alert, buttons included, so an assistive-technology user hears
 * the options with the problem.
 *
 * @param props see {@link QueryErrorProps}
 * @returns the alert, or nothing when there is no error
 */
export function QueryError({
  error,
  subject,
  onRetry,
  retrying = false,
  retryLabel = 'Try again',
  onReload,
  reloadLabel = 'Reload current values',
  testId,
}: QueryErrorProps): React.JSX.Element | null {
  if (error === null) {
    return null;
  }

  const reload = isConflictError(error) ? onReload : undefined;
  const details = supportDetails(error);

  return (
    <div className="fo-query-error" role="alert" data-testid={testId}>
      <p>
        {subject === undefined ? error.message : `${subject} could not be read: ${error.message}`}
      </p>
      {subject === undefined ? null : (
        <p>
          Nothing is shown below because nothing is known — this is not the same as there being
          nothing there.
        </p>
      )}
      {onRetry === undefined && reload === undefined ? null : (
        <div className="fo-query-error__actions">
          {onRetry === undefined ? null : (
            <Button busy={retrying} onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          {reload === undefined ? null : <Button onClick={reload}>{reloadLabel}</Button>}
        </div>
      )}
      {details === null ? null : (
        <details>
          <summary>Error details</summary>
          <p>
            HTTP {details.status}
            {details.reference === null ? null : <> · Reference: {details.reference}</>}
          </p>
        </details>
      )}
    </div>
  );
}
